/**
 * RowDetailDialog — per-row triage without touching the CSV file.
 *
 * Opens from any preview-table row. Top section shows the ORIGINAL
 * data exactly as it appears in the file — each value with its own
 * column header above it, so the user knows what everything means.
 * Below it, an editable "Import as" form pre-filled with the row's
 * current values: fix a field, pull a name from the email, or drop
 * the row from the import entirely (reversible).
 */

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { nameFromEmail, type ImportFieldDescriptor } from '../../lib/csv-mapping';

const EMAIL_SHAPE_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Props {
  open: boolean;
  rowIndex: number;
  /** The row exactly as parsed from the file. */
  rawRow: Record<string, string>;
  csvHeaders: string[];
  /** Target-field descriptors for the entity being imported. */
  fields: ReadonlyArray<ImportFieldDescriptor>;
  /** The row's current effective target-shaped values (mapping + edits). */
  currentValues: Record<string, string>;
  isDropped: boolean;
  onApply: (values: Record<string, string>) => void;
  onDrop: () => void;
  onRestore: () => void;
  onClose: () => void;
}

export function RowDetailDialog({
  open,
  rowIndex,
  rawRow,
  csvHeaders,
  fields,
  currentValues,
  isDropped,
  onApply,
  onDrop,
  onRestore,
  onClose,
}: Props) {
  const [draft, setDraft] = useState<Record<string, string>>(currentValues);

  // Re-seed the form whenever the dialog opens on a (possibly new) row.
  // Deliberately keyed on open/rowIndex only — currentValues changing
  // mid-edit (preview refresh) must not clobber the user's draft.
  useEffect(() => {
    if (open) setDraft(currentValues);
  }, [open, rowIndex]);

  const hasFirstNameField = fields.some((f) => f.field === 'firstName');
  const draftEmail = (draft.primaryEmail ?? '').split(/[,;]/)[0]?.trim() ?? '';
  const canSuggestName =
    hasFirstNameField && !draft.firstName?.trim() && EMAIL_SHAPE_RE.test(draftEmail);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle>Row {rowIndex + 1} from your file</DialogTitle>
        </DialogHeader>

        <section>
          <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Original data
          </h3>
          <div className="flex flex-wrap gap-x-8 gap-y-3 rounded-lg bg-muted/40 p-3">
            {csvHeaders.map((header) => (
              <div key={header} className="min-w-[8rem] max-w-full">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  {header}
                </p>
                <p className="break-all text-sm">
                  {rawRow[header]?.trim() ? (
                    rawRow[header]
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Import as
            </h3>
            {canSuggestName && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setDraft((d) => ({ ...d, firstName: nameFromEmail(draftEmail) }))}
              >
                Use email as first name
              </Button>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {fields.map((f) => (
              <label key={f.field} className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">
                  {f.label}
                  {f.required && <span className="text-destructive"> *</span>}
                </span>
                <Input
                  value={draft[f.field] ?? ''}
                  onChange={(e) => setDraft((d) => ({ ...d, [f.field]: e.target.value }))}
                  disabled={isDropped}
                  aria-label={`${f.label} value`}
                />
              </label>
            ))}
          </div>
        </section>

        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-between">
          {isDropped ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onRestore();
                onClose();
              }}
            >
              Restore this row
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              className="text-destructive"
              onClick={() => {
                onDrop();
                onClose();
              }}
            >
              Drop this row
            </Button>
          )}
          <div className="flex gap-2 sm:ml-auto">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={isDropped}
              onClick={() => {
                const values: Record<string, string> = {};
                for (const [field, value] of Object.entries(draft)) {
                  if (value.trim() !== '') values[field] = value.trim();
                }
                onApply(values);
                onClose();
              }}
            >
              Apply changes
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
